import { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router";
import { Body, Button, ButtonGroup, H2, H3 } from "@datapay-by-datacom/design-system";
import { ImportProgress } from "../../shared";
import { SplitCostingWizardCtx } from "../components/SplitCostingWizardContext";
import type { SplitCostingImportError } from "../hooks/useSplitCostingImportInvocation";
import type { SplitCostingRow } from "../utils";
import styles from "./ResultScreen.module.css";

const UPLOAD_ROUTE = "/onboarding/split-costing-import";

interface DisplayFailure {
  fileRow: number | null;
  employeeCode: string | null;
  field: string | null;
  message: string;
}

interface ResultScreenProps {
  /** Single-page experience resets in place instead of navigating. */
  onStartNewImport?: () => void;
}

/**
 * Recovers the original file row number for a commit error by joining against
 * the rows sent on invoke (`error.index` is the 0-based position in that
 * array) — mirrors `SplitCostingValidationReport`'s equivalent join. Falls
 * back to a 1-based index when the rows aren't available (e.g. the wizard
 * state was cleared between commit and this screen rendering).
 */
function toDisplayFailure(
  error: SplitCostingImportError,
  rows: SplitCostingRow[] | null
): DisplayFailure {
  const row = error.index !== null ? (rows?.[error.index] ?? null) : null;
  return {
    fileRow: row?.fileRow ?? (error.index !== null ? error.index + 1 : null),
    employeeCode: error.employeeCode,
    field: error.field,
    message: error.error,
  };
}

/**
 * Final step of the split costing import wizard — Result.
 *
 * Rendered as a child route of `SplitCostingWizardProvider`. `ValidateScreen`
 * navigates here immediately on "Continue to commit", without awaiting the
 * commit poll — the `manager` instance is lifted into `SplitCostingWizardCtx`
 * (see that provider's doc comment), so the same in-flight commit poll
 * continues here rather than restarting. A commit over hundreds of rows
 * should not look frozen, so this screen renders its own in-progress state
 * while `manager.isCommitting` is true.
 *
 * Renders the committed/failed row counts once the commit workflow reaches
 * `COMPLETED`, with a per-row failure table if any rows failed.
 */
export function ResultScreen({ onStartNewImport }: ResultScreenProps = {}) {
  const wizardCtx = useContext(SplitCostingWizardCtx);
  const navigate = useNavigate();

  if (!wizardCtx) {
    throw new Error("ResultScreen must be rendered inside SplitCostingWizardProvider");
  }

  const { companyCode, companyName, parsedRows, clearUpload, manager } = wizardCtx;
  const { commitResult, isCommitting, managerError } = manager;

  const failures = useMemo<DisplayFailure[]>(
    () =>
      (commitResult?.errors ?? [])
        .map((error) => toDisplayFailure(error, parsedRows))
        .sort((a, b) => (a.fileRow ?? 0) - (b.fileRow ?? 0)),
    [commitResult, parsedRows]
  );

  // Leaving the result screen (in any state) starts a fresh wizard cycle —
  // clear both the manager's invocation state (idempotency key, validation
  // report, commit result) and the wizard's upload state so a new file
  // doesn't inherit anything from this run.
  const handleStartNewImport = useCallback(() => {
    if (onStartNewImport) {
      onStartNewImport();
      return;
    }
    manager.clear();
    clearUpload();
    navigate(UPLOAD_ROUTE);
    // `manager` intentionally excluded — mirrors `ValidateScreen`'s handlers:
    // its functions are re-created every render, so including it would just
    // add churn to this callback's identity without changing behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearUpload, navigate, onStartNewImport]);

  if (managerError) {
    return (
      <div className={styles.container}>
        <H2>Commit result</H2>
        <div className={styles.errorBanner} role="alert">
          <strong>Commit failed.</strong> {managerError}
        </div>
        <ButtonGroup align="right">
          <Button variant="primary" onPress={handleStartNewImport}>
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  // A direct visit (or refresh) that cleared wizard state — no commit has
  // been started in this session. Give the user a clear exit rather than an
  // indefinite spinner.
  if (!isCommitting && !commitResult) {
    return (
      <div className={styles.container}>
        <H2>Commit result</H2>
        <Body>No commit is in progress. Start a new import to continue.</Body>
        <ButtonGroup align="right">
          <Button variant="primary" onPress={handleStartNewImport}>
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  // Still polling — commit is in flight.
  if (isCommitting) {
    return (
      <div className={styles.container}>
        <ImportProgress
          heading="Committing import…"
          progressbarLabel="Commit in progress"
          detail={
            <>
              Submitting split costing rows for{" "}
              <strong>{companyName ?? companyCode}</strong>. This can take a
              moment for larger files.
            </>
          }
          notice="You don't need to wait on the previous screen."
        />
      </div>
    );
  }

  // TypeScript can't narrow `commitResult` through the combined early-return
  // guards above. At this point: !managerError, !isCommitting, and the
  // `!isCommitting && !commitResult` guard already exited — so commitResult is
  // guaranteed non-null. This guard is unreachable at runtime.
  if (!commitResult) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Commit result</H2>
        <Body>
          Split costing import for <strong>{companyName ?? companyCode}</strong>.
        </Body>
      </div>

      <section className={styles.summarySection} aria-label="Commit summary">
        <div className={styles.summaryGrid}>
          <div
            className={`${styles.summaryCard} ${commitResult.succeeded > 0 ? styles.summaryCardCommitted : ""}`}
          >
            <span className={styles.summaryCount}>{commitResult.succeeded}</span>
            <span className={styles.summaryLabel}>Rows committed</span>
          </div>
          <div
            className={`${styles.summaryCard} ${commitResult.failed > 0 ? styles.summaryCardFailed : ""}`}
          >
            <span className={styles.summaryCount}>{commitResult.failed}</span>
            <span className={styles.summaryLabel}>Rows failed</span>
          </div>
        </div>
      </section>

      {/*
       * Partial failure — Christmas commits split costing as a single
       * all-or-nothing operation (LADR034), so `succeeded > 0` alongside
       * `failed > 0` should be structurally impossible. Rendered defensively
       * anyway: if it somehow happens, say plainly that some rows did not
       * apply rather than showing what would otherwise read as a bare
       * success message next to an unrelated failure table.
       */}
      {commitResult.succeeded > 0 && commitResult.failed > 0 && (
        <div className={styles.partialFailureBanner} role="alert">
          <strong>Some rows did not apply.</strong> Split costing imports
          commit as a single all-or-nothing operation in Christmas, so a
          result with both committed and failed rows should not be possible.
          Do not assume the {commitResult.succeeded} row
          {commitResult.succeeded === 1 ? "" : "s"} reported as committed
          actually applied — check <strong>Employee maintenance → Costing</strong>{" "}
          in Christmas for <strong>{companyName ?? companyCode}</strong>{" "}
          carefully before relying on these counts, and contact support.
        </div>
      )}

      {commitResult.succeeded > 0 && commitResult.failed === 0 && (
        <Body>
          To verify, open <strong>Employee maintenance → Costing</strong> in
          Christmas for <strong>{companyName ?? companyCode}</strong> and
          confirm {commitResult.succeeded} new split costing row
          {commitResult.succeeded === 1 ? "" : "s"}. The portal doesn&apos;t
          show committed splits, so Christmas is the source of truth for this
          check.
        </Body>
      )}

      {failures.length > 0 && (
        <section aria-labelledby="commit-failures-heading">
          <H3 id="commit-failures-heading">
            {failures.length} row{failures.length === 1 ? "" : "s"} failed
          </H3>
          <div
            className={styles.tableWrapper}
            role="region"
            aria-label="Failed rows"
            tabIndex={0}
          >
            <table className={styles.failuresTable}>
              <thead>
                <tr>
                  <th scope="col">Row</th>
                  <th scope="col">Employee code</th>
                  <th scope="col">Field</th>
                  <th scope="col">Message</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((failure) => (
                  <tr key={`${failure.fileRow ?? "null"}-${failure.employeeCode ?? "null"}-${failure.field ?? "null"}-${failure.message}`}>
                    <td className={styles.cell}>{failure.fileRow ?? "—"}</td>
                    <td className={styles.cell}>{failure.employeeCode ?? "—"}</td>
                    <td className={styles.cell}>{failure.field ?? "—"}</td>
                    <td className={styles.cell}>{failure.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <ButtonGroup align="right">
        <Button variant="primary" onPress={handleStartNewImport}>
          Start a new import
        </Button>
      </ButtonGroup>
    </div>
  );
}
